import { useCallback, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Check, Plus, Trash2, X } from "lucide-react"
import { Field } from "../../shared/components/EditorFields"
import { type EntityKind, type ServiceStore, displayName } from "../types"
import * as db from "../store"

/**
 * Field controls shared by the service and provider editors.
 *
 * EntityPicker and MultiPicker resolve ids to display names themselves, so a
 * caller passes the id list and the option set rather than pre-formatted
 * labels. RowList handles the repeated add/remove row pattern (hours,
 * contact channels, consent forms).
 */

export function EntityPicker({
  store, locale, kind, options, value, onChange, onOpen, placeholder, disabled,
}: {
  store: ServiceStore
  locale: string
  /** Kind of the options, used to resolve a selected id missing from the list. */
  kind: EntityKind
  options: any[]
  value?: string
  onChange: (v: string | undefined) => void
  onOpen?: (v: string) => void
  placeholder: string
  disabled?: boolean
}) {
  const NONE = "__none__"

  // The selected record may be filtered out of `options` — a provider with no
  // country, say. Append it rather than render a blank trigger that looks like
  // the link was lost.
  const shown = useMemo(() => {
    if (!value || options.some((o: any) => o.id === value)) return options
    const current = db.getEntity(store, kind, value)
    return current ? [...options, current] : options
  }, [options, value, store, kind])

  return (
    <div className="flex items-center gap-1">
      <Select
        value={value ?? NONE}
        onValueChange={v => onChange(v === NONE ? undefined : v)}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 flex-1 text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE} className="text-sm text-muted-foreground">{placeholder}</SelectItem>
          {shown.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              None available — create one from the browser first.
            </div>
          )}
          {shown.map((o: any) => (
            <SelectItem key={o.id} value={o.id} className="text-sm">
              {displayName(o, locale, store.defaultLocale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value && onOpen && (
        <Button variant="ghost" size="sm" className="h-8 shrink-0 px-2 text-xs" onClick={() => onOpen(value)}>
          Open
        </Button>
      )}
    </div>
  )
}

/** M2M picker: chips for what's selected, a dropdown to add. */

export function MultiPicker({
  store, locale, kind, value, onChange, filter, hint,
}: {
  store: ServiceStore
  locale: string
  kind: EntityKind
  value: string[]
  onChange: (ids: string[]) => void
  filter?: (e: any) => boolean
  hint?: string
}) {
  const [open, setOpen] = useState(false)
  const all = useMemo(() => {
    const rows = db.listEntities(store, kind)
    return filter ? rows.filter(filter) : rows
  }, [store, kind, filter])

  const label = useCallback(
    (id: string) => {
      const e = db.getEntity(store, kind, id)
      return e ? displayName(e, locale, store.defaultLocale) : id
    },
    [store, kind, locale],
  )

  const available = all.filter((o: any) => !value.includes(o.id))

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {value.map(id => (
          <Badge key={id} variant="secondary" className="gap-1 text-xs">
            {label(id)}
            <button
              type="button"
              onClick={() => onChange(value.filter(v => v !== id))}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {value.length === 0 && <span className="text-xs text-muted-foreground">None selected</span>}
      </div>

      {open ? (
        <div className="max-h-40 overflow-y-auto rounded border">
          {available.length === 0 ? (
            <p className="p-2 text-xs text-muted-foreground">Nothing left to add.</p>
          ) : (
            available.map((o: any) => (
              <button
                type="button"
                key={o.id}
                onClick={() => { onChange([...value, o.id]); setOpen(false) }}
                className="flex w-full items-center gap-2 px-2 py-1 text-left text-sm hover:bg-muted/60"
              >
                <Check className="h-3 w-3 opacity-0" />
                {displayName(o, locale, store.defaultLocale)}
              </button>
            ))
          )}
        </div>
      ) : (
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>
          <Plus className="mr-1 h-3 w-3" /> Add
        </Button>
      )}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

/** Editable list of JSON rows (contact channels, opening hours). */

export function RowList<T extends Record<string, any>>({
  rows, onChange, blank, addLabel, render,
}: {
  rows: T[]
  onChange: (rows: T[]) => void
  blank: T
  addLabel: string
  render: (row: T, update: (patch: Partial<T>) => void) => React.ReactNode
}) {
  return (
    <div className="space-y-1">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1">
          {render(row, patch => {
            const next = [...rows]
            next[i] = { ...row, ...patch }
            onChange(next)
          })}
          <button
            type="button"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button
        variant="outline" size="sm" className="h-7 text-xs"
        onClick={() => onChange([...rows, { ...blank }])}
      >
        <Plus className="mr-1 h-3 w-3" /> {addLabel}
      </Button>
    </div>
  )
}
